import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DataTable } from '@/components/DataTable';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { appointmentsService } from '@/services/appointments';
import { doctorsService } from '@/services/doctors';
import { patientsService } from '@/services/patients';
import { getApiErrorMessage } from '@/lib/api';
import { Plus } from '@phosphor-icons/react';
import { formatDate } from '@/lib/utils';
import { Skeleton } from '@/components/ui/skeleton';
import { motion } from 'framer-motion';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';

export default function AppointmentsPage() {
  const navigate = useNavigate();
  const [appointments, setAppointments] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [patients, setPatients] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedPatient, setSelectedPatient] = useState('');
  const [selectedDoctor, setSelectedDoctor] = useState('');
  const [selectedDate, setSelectedDate] = useState('');
  const [selectedSlot, setSelectedSlot] = useState('');
  const [aptType, setAptType] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [appointmentsRes, doctorsRes, patientsRes] = await Promise.all([
          appointmentsService.getAll(),
          doctorsService.getAll(),
          patientsService.getAll(),
        ]);
        setAppointments(appointmentsRes.data);
        setDoctors(doctorsRes.data);
        setPatients(patientsRes.data);
      } catch (error) {
        toast.error(getApiErrorMessage(error));
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchSlots = async () => {
      if (selectedDoctor && selectedDate) {
        try {
          const response = await doctorsService.getAvailableSlots(selectedDoctor, selectedDate);
          setAvailableSlots(response.data);
        } catch (error) {
          toast.error(getApiErrorMessage(error));
        }
      }
    };

    fetchSlots();
  }, [selectedDoctor, selectedDate]);

  const columns = [
    { key: 'patientName', header: 'Patient', sortable: true },
    {
      key: 'doctorName',
      header: 'Doctor',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <Avatar className="h-8 w-8">
            <AvatarImage src={doctors.find(d => d.name === value)?.avatar} />
            <AvatarFallback>{value?.charAt(0)}</AvatarFallback>
          </Avatar>
          <span>{value}</span>
        </div>
      ),
    },
    {
      key: 'department',
      header: 'Department',
      sortable: true,
      render: (value) => <Badge variant="outline">{value}</Badge>,
    },
    {
      key: 'date',
      header: 'Date',
      sortable: true,
      render: (value) => formatDate(value),
    },
    { key: 'time', header: 'Time' },
    {
      key: 'type',
      header: 'Type',
      render: (value) => <Badge variant="secondary">{value}</Badge>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (value) => {
        const variants = {
          Scheduled: 'default',
          Completed: 'secondary',
          Cancelled: 'destructive',
        };
        return <Badge variant={variants[value] || 'default'}>{value}</Badge>;
      },
    },
  ];

  const handleBookAppointment = async () => {
    if (!selectedPatient || !selectedDoctor || !selectedDate || !selectedSlot || !aptType) {
      toast.error('Please fill all fields');
      return;
    }
    try {
      await appointmentsService.create({
        patientId: selectedPatient,
        doctorId: selectedDoctor,
        date: selectedDate,
        time: selectedSlot,
        type: aptType,
      });
      toast.success('Appointment booked successfully');
      setIsDialogOpen(false);
      setSelectedPatient('');
      setSelectedDoctor('');
      setSelectedDate('');
      setSelectedSlot('');
      setAptType('');
      const r = await appointmentsService.getAll();
      setAppointments(r.data);
    } catch (error) {
      toast.error(getApiErrorMessage(error));
    }
  };

  const todayStr = new Date().toISOString().split('T')[0];
  const todayApts = appointments.filter((a) => a.date === todayStr);
  const totalToday = todayApts.length;
  const scheduledCount = todayApts.filter((a) => a.status === 'Scheduled').length;
  const completedCount = todayApts.filter((a) => a.status === 'Completed').length;
  const cancelledCount = todayApts.filter((a) => a.status === 'Cancelled').length;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-full" />
        <Skeleton className="h-96 w-full" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="appointments-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-light tracking-tighter mb-2">Appointments</h1>
          <p className="text-sm md:text-base leading-relaxed text-muted-foreground">
            Manage patient appointments and schedules
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-appointment-button">
              <Plus className="mr-2 h-4 w-4" weight="bold" />
              Schedule Appointment
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Schedule Appointment</DialogTitle>
              <DialogDescription>
                Book an appointment with available doctor and time slot
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Patient</Label>
                <Select value={selectedPatient} onValueChange={setSelectedPatient}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select patient" />
                  </SelectTrigger>
                  <SelectContent>
                    {patients.map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="apt-doctor">Select Doctor</Label>
                <Select value={selectedDoctor} onValueChange={setSelectedDoctor}>
                  <SelectTrigger>
                    <SelectValue placeholder="Choose a doctor" />
                  </SelectTrigger>
                  <SelectContent>
                    {doctors.map((doctor) => (
                      <SelectItem key={doctor.id} value={doctor.id}>
                        <div className="flex items-center gap-2">
                          <Avatar className="h-6 w-6">
                            <AvatarImage src={doctor.avatar} />
                            <AvatarFallback>{doctor.name?.charAt(0)}</AvatarFallback>
                          </Avatar>
                          <div>
                            <p className="font-medium">{doctor.name}</p>
                            <p className="text-xs text-muted-foreground">{doctor.specialization}</p>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="apt-date">Date</Label>
                <Input
                  id="apt-date"
                  type="date"
                  value={selectedDate}
                  onChange={(e) => {
                    setSelectedDate(e.target.value);
                    setSelectedSlot(''); // Reset slot when date changes
                  }}
                  min={new Date().toISOString().split('T')[0]}
                />
              </div>

              {selectedDoctor && selectedDate && (
                <div className="space-y-2">
                  <Label>Available Time Slots</Label>
                  {availableSlots.length > 0 ? (
                    <div className="grid grid-cols-4 gap-2">
                      {availableSlots.map((slot) => (
                        <Button
                          key={slot}
                          variant={selectedSlot === slot ? 'default' : 'outline'}
                          className="w-full"
                          onClick={() => setSelectedSlot(slot)}
                        >
                          {slot}
                        </Button>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">No slots available for this date</p>
                  )}
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="apt-type">Appointment Type</Label>
                <Select value={aptType} onValueChange={setAptType}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Consultation">Consultation</SelectItem>
                    <SelectItem value="Follow-up">Follow-up</SelectItem>
                    <SelectItem value="Check-up">Check-up</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Button className="w-full" onClick={handleBookAppointment}>
                Book Appointment
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4 mb-6">
        {[
          { label: 'Total Today', value: String(totalToday), color: 'text-primary' },
          { label: 'Scheduled', value: String(scheduledCount), color: 'text-secondary' },
          { label: 'Completed', value: String(completedCount), color: 'text-success' },
          { label: 'Cancelled', value: String(cancelledCount), color: 'text-destructive' },
        ].map((stat, index) => (
          <motion.div
            key={stat.label}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="border rounded-md p-4 hover-lift bg-card">
              <p className="text-[10px] md:text-xs font-semibold uppercase tracking-[0.2em] text-muted-foreground mb-1">
                {stat.label}
              </p>
              <p className={`text-2xl md:text-3xl font-medium tracking-tight ${stat.color}`}>
                {stat.value}
              </p>
            </div>
          </motion.div>
        ))}
      </div>

      <DataTable
        data={appointments}
        columns={columns}
        searchPlaceholder="Search appointments..."
        searchKeys={[
          'id',
          'patientName',
          'doctorName',
          'department',
          'date',
          'time',
          'type',
          'status',
        ]}
        onRowClick={(row) => navigate(`/appointments/${row.id}`)}
      />
    </div>
  );
}
